// Foundations
import { Component, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';

// rxjs
import { finalize, map } from 'rxjs/operators';
import { Observable } from 'rxjs';

// Services
import { AnalyticsService } from '@app/services/analytics.service';
import { CognitoService, FLAG_ACCT_IN_DD } from '@app/services/cognito.service';
import {
  GetAccountPhonesIdGQL,
  UpdateAccountGQL,
  AddAccountGQL,
} from '@app/services/graphql.service';
import { RegisterService } from '@app/services/register.service';
import { LoadingIndicatorService } from '@app/services/loading-indicator.service';
import { AuthService } from '@app/auth/auth.service';

// Components
import { CampLocationSearchSelectComponent } from '@app/components/_share/camp-location-search-select/camp-location-search-select.component';

@Component({
  selector: 'app-location-selection',
  templateUrl: './location-selection.component.html',
  styleUrls: ['./location-selection.component.scss'],
})
export class LocationSelectionComponent implements OnInit {
  @ViewChild('searchSelect', { static: false })
  searchSelect: CampLocationSearchSelectComponent;

  selectedCampId: number;
  isBtnEnabled = false;

  primaryPhoneId$: Observable<string>;

  constructor(
    private snackBar: MatSnackBar,
    private router: Router,
    private _analytics: AnalyticsService,
    private registerService: RegisterService,
    private cognitoService: CognitoService,
    private authService: AuthService,
    private getAccountPhonesIdQuery: GetAccountPhonesIdGQL,
    private addAccountMutation: AddAccountGQL,
    private updateAccountMutation: UpdateAccountGQL,
    private loadingIndicatorService: LoadingIndicatorService
  ) {}

  // Life cycles
  // ------------------------------------------------------------------------------------

  ngOnInit(): void {
    this._analytics.logScreen('regLocationSelectionPage');
  }

  // Public Methods
  // ------------------------------------------------------------------------------------

  onContinue(): void {
    this._analytics.logButtonTap('regLocationSelection_saveAndContinue');

    if (this.selectedCampId) {
      this.registerService.updateRegisterFormData({
        locationId: this.selectedCampId,
      });

      const { locationId, firstName, lastName, email, password, phone } =
        this.registerService.readRegisterFormData();

      this.loadingIndicatorService.show();

      this.cognitoService.signIn({ username: email, password }).subscribe(
        (status) => {
          if (status.result === 'SUCCESS') {
            this.authService.fetchAccountId().subscribe((accountId) => {
              if (accountId) {
                // if email exists (= account exists),
                // update account info, set FLAG_ACCT_IN_DD to TRUE, and navigate to confirmation
                this.primaryPhoneId$ = this.getAccountPhonesIdQuery
                  .fetch({ accountId })
                  .pipe(
                    map((res) => {
                      const phones = res.data.getAccount.phones;
                      return phones?.length > 0 ? phones[0].id : null;
                    })
                  );

                this.primaryPhoneId$.subscribe((primaryPhoneId) => {
                  this.updateAccountMutation
                    .mutate({
                      accountId,
                      accountInput: {
                        preferredFranchise: locationId,
                        firstName,
                        lastName,
                        email,
                        phones: [
                          {
                            ...(!!primaryPhoneId && { id: primaryPhoneId }),
                            geoCountry: '228',
                            phoneNumber: phone.phoneNumber,
                            phoneType: phone.phoneType.id,
                            isPrimary: true,
                            isVerified: false,
                            marketingSmsOptIn: false,
                          },
                        ],
                      },
                      forRegistration: true,
                    })
                    .subscribe((res) => {
                      if (res.data) {
                        this.updateAcctInDDFlag();
                      } else {
                        this.loadingIndicatorService.hide();
                        this.cognitoService.signOut();
                      }
                    });
                });
              } else {
                // if email does not exist (= account does not exist)
                // create new account with info, set FLAG_ACCT_IN_DD to TRUE, and navigate to confirmation
                this.addAccountMutation
                  .mutate({
                    accountInput: {
                      preferredFranchise: locationId,
                      firstName,
                      lastName,
                      email,
                      phones: [
                        {
                          geoCountry: '228',
                          phoneNumber: phone.phoneNumber,
                          phoneType: phone.phoneType.id,
                          isPrimary: true,
                          isVerified: false,
                          marketingSmsOptIn: false,
                        },
                      ],
                    },
                  })
                  .subscribe((res) => {
                    if (res.data) {
                      this.updateAcctInDDFlag();
                    } else {
                      this.loadingIndicatorService.hide();
                      this.cognitoService.signOut();
                    }
                  });
              }
            });
          } else {
            this.loadingIndicatorService.hide();
            this.snackBar.open(status.message, '', { duration: 2000 });
          }
        },
        (error) => {
          this.loadingIndicatorService.hide();
        }
      );
    }
  }

  handleSelectedCampId(id: number): void {
    this.isBtnEnabled = !!id;
    this.selectedCampId = id;
  }

  // Private Methods
  // ------------------------------------------------------------------------------------

  private updateAcctInDDFlag() {
    const { email, password } = this.registerService.readRegisterFormData();
    this.cognitoService
      .updateCognitoAttrValue(
        { username: email, password },
        FLAG_ACCT_IN_DD,
        'TRUE'
      )
      .pipe(
        finalize(() => {
          this.loadingIndicatorService.hide();
          this.cognitoService.signOut();
        })
      )
      .subscribe((status) => {
        if (status.result === 'SUCCESS') {
          this.router.navigate(['/register/confirm']);
        } else {
          this.snackBar.open(status.message, '', { duration: 2000 });
        }
      });
  }
}
